/**
 * "+ Add tile" slot — the empty-zone affordance the user described.
 *
 * Behavior matches main's slide-builder feel without pulling in
 * dnd-kit: hover an empty zone (between tiles or at the dashboard
 * bottom) → a large "+" call-to-action expands; click it → a chat
 * dialog opens where the user describes the tile in natural language.
 *
 * The slot itself doesn't talk to the agent — it surfaces the user's
 * description (and the insertion index) via ``onAddTile`` so the
 * parent renderer can dispatch into whatever chat / yaml-edit pipeline
 * is wired. This keeps the slot reusable across the live dashboard
 * and any preview / edit-mode surface that wants the same UX.
 */

import { useState } from "react"

import { Plus, Sparkles } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { flushSync } from "react-dom"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

import type { DashboardElement } from "../dashboard/catalog"

import { ManualTileForm } from "./manual-tile-form"

export interface AddTileSlotProps {
  /**
   * Where the new tile should be inserted in the parent's children
   * list. Use ``children.length`` for "append at the bottom"; use
   * the index of an existing tile to insert before it.
   */
  insertionIndex: number
  /**
   * Called with the user's description and the insertion index when
   * they submit the dialog. The parent dispatches into the chat
   * pipeline (yaml-edit fence) to generate one tile and splice it in.
   */
  onAddTile?: (description: string, insertionIndex: number) => void
  /** Adds a fully-authored static tile without invoking the assistant. */
  onAddManualTile?: (tile: DashboardElement, insertionIndex: number) => void
  /**
   * Visual variant. ``"between"`` is a thin in-line slot used between
   * existing tiles (low-key, only fully visible on hover).
   * ``"end"`` is the larger slot at the dashboard's tail end —
   * always partially visible so a freshly-loaded empty board has a
   * clear "click here" target.
   */
  variant?: "between" | "end"
  className?: string
}

export function AddTileSlot({
  insertionIndex,
  onAddTile,
  onAddManualTile,
  variant = "between",
  className,
}: AddTileSlotProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState(onAddManualTile ? "manual" : "ai")

  const placeholder = t("dash.add_tile.placeholder", {
    defaultValue: "e.g. bar chart of revenue by region for the last quarter",
  })

  function handleSubmit() {
    const trimmed = description.trim()
    if (!trimmed || submitting || !onAddTile) return
    setSubmitting(true)
    try {
      onAddTile(trimmed, insertionIndex)
    } finally {
      // Local state cleanup — the parent's response arrives via the
      // chat stream, not through this dialog.
      setSubmitting(false)
      setOpen(false)
      setDescription("")
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setDescription("")
      setActiveTab(onAddManualTile ? "manual" : "ai")
    }
    setOpen(next)
  }

  function handleManualCreate(tile: DashboardElement) {
    if (!onAddManualTile) return
    // The parent mutation reconciles the end/between slots immediately.
    // Commit the controlled Dialog's closed state first so its portal cannot
    // survive that reconciliation with stale `open=true` local state.
    flushSync(() => {
      handleOpenChange(false)
    })
    onAddManualTile(tile, insertionIndex)
    toast.success(t("dash.add_tile.added", { defaultValue: "Tile added" }))
  }

  return (
    <>
      <button
        aria-label={t("dash.add_tile.cta", { defaultValue: "Add a new tile" })}
        className={cn(
          "group relative flex w-full items-center justify-center rounded-md border-2 border-dashed transition-[height,margin,border-color,background-color]",
          variant === "between"
            ? // Subtle between-tile divider; collapses to a thin line at rest,
              // expands on hover so it doesn't compete with the surrounding
              // tile titles when the user is scanning.
              "hover:border-primary/30 hover:bg-primary/[0.03] -my-0.5 h-6 border-transparent hover:h-12"
            : // End-of-dashboard target; always partially visible so an
              // empty dashboard has an obvious entry point.
              "border-muted-foreground/20 hover:border-primary/40 hover:bg-primary/[0.04] mt-3 h-20",
          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
          className
        )}
        type="button"
        onClick={() => setOpen(true)}
      >
        <span
          className={cn(
            "text-muted-foreground group-hover:text-primary inline-flex items-center gap-2 text-sm transition-opacity",
            // Between-variant icon stays hidden until hover so the line
            // is genuinely subtle when the user isn't looking at it.
            variant === "between"
              ? "opacity-0 group-hover:opacity-100"
              : "opacity-80 group-hover:opacity-100"
          )}
        >
          <Plus className={variant === "end" ? "size-5" : "size-4"} />
          {t("dash.add_tile.cta", { defaultValue: "Add tile" })}
        </span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="text-primary size-4" />
              {t("dash.add_tile.dialog_title", { defaultValue: "Add tile" })}
            </DialogTitle>
            <DialogDescription>
              {t("dash.add_tile.dialog_hint", {
                defaultValue:
                  "Create the tile yourself or let the assistant generate it from your description.",
              })}
            </DialogDescription>
          </DialogHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              {onAddManualTile && (
                <TabsTrigger className="flex-1" value="manual">
                  <Plus className="size-4" />
                  {t("dash.add_tile.manual_tab", { defaultValue: "Create manually" })}
                </TabsTrigger>
              )}
              {onAddTile && (
                <TabsTrigger className="flex-1" value="ai">
                  <Sparkles className="size-4" />
                  {t("dash.add_tile.ai_tab", { defaultValue: "Create with AI" })}
                </TabsTrigger>
              )}
            </TabsList>

            {onAddManualTile && (
              <TabsContent value="manual">
                <ManualTileForm
                  onCancel={() => handleOpenChange(false)}
                  onCreate={handleManualCreate}
                />
              </TabsContent>
            )}

            {onAddTile && (
              <TabsContent value="ai">
                <div className="flex flex-col gap-4">
                  <Input
                    autoFocus
                    disabled={submitting}
                    placeholder={placeholder}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault()
                        handleSubmit()
                      }
                    }}
                  />
                  <DialogFooter>
                    <Button
                      disabled={submitting}
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                    >
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button disabled={!description.trim() || submitting} onClick={handleSubmit}>
                      <Sparkles className="mr-1.5 size-3.5" />
                      {t("dash.add_tile.submit", { defaultValue: "Generate" })}
                    </Button>
                  </DialogFooter>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  )
}
